import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from "react-native";
import {
  Activity,
  CalendarDays,
  Check,
  Clock3,
  FileText,
} from "lucide-react-native";
import {
  Dialog,
  DialogHeader,
  DialogPrimaryButton,
  DialogSecondaryButton,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
  MoonTimeField,
} from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { EMBER } from "../../onboarding/theme";
import {
  createIdempotencyKey,
  useRecordSpecimenMaterialOperation,
  useSpecimens,
  type SpecimenMaterialOperation,
  type SpecimenMaterialOperationAction,
} from "@src/packages/care/cryovault/useSpecimens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type {
  CryoSpecimenDetail,
  CryoSpecimenType,
} from "./CryoSpecimenDetailModal";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// TODO: replace this extension when the pinned client contract includes the
// dashboard thawingInfo.survivedQuantity field.
type TypedThawingInfo = NonNullable<
  SpecimenMaterialOperation["thawingInfo"]
> & {
  survivedQuantity?: number | null;
};
type TypedSpecimenMaterialOperation = Omit<
  SpecimenMaterialOperation,
  "thawingInfo"
> & {
  thawingInfo?: TypedThawingInfo | null;
};

const LIFECYCLE_ACTIONS: Record<
  CryoSpecimenType,
  readonly SpecimenMaterialOperationAction[]
> = {
  embryo: [
    "reserve",
    "thaw",
    "transfer",
    "use",
    "biopsy",
    "test",
    "culture",
    "freeze",
    "donate",
    "discard",
    "release",
  ],
  oocyte: [
    "reserve",
    "thaw",
    "mature",
    "fertilize",
    "use",
    "freeze",
    "donate",
    "discard",
    "release",
  ],
  sperm: [
    "reserve",
    "thaw",
    "use",
    "test",
    "freeze",
    "donate",
    "discard",
    "release",
  ],
};

const LIFECYCLE_LABEL_KEYS: Record<SpecimenMaterialOperationAction, string> = {
  thaw: "cryo_vault.modal.lifecycle_event_thaw",
  donate: "cryo_vault.modal.lifecycle_event_donate",
  fertilize: "cryo_vault.modal.lifecycle_event_fertilize",
  mature: "cryo_vault.modal.lifecycle_event_mature",
  discard: "cryo_vault.modal.lifecycle_event_discard",
  use: "cryo_vault.modal.lifecycle_event_use",
  release: "cryo_vault.modal.lifecycle_event_release",
  test: "cryo_vault.modal.lifecycle_event_test",
  culture: "cryo_vault.modal.lifecycle_event_culture",
  transfer: "cryo_vault.modal.lifecycle_event_transfer",
  biopsy: "cryo_vault.modal.lifecycle_event_biopsy",
  freeze: "cryo_vault.modal.lifecycle_event_freeze",
  reserve: "cryo_vault.modal.lifecycle_event_reserve",
};

export function getLifecycleActionsForSpecimen(
  specimen: CryoSpecimenDetail,
): readonly SpecimenMaterialOperationAction[] {
  return LIFECYCLE_ACTIONS[specimen.type];
}

function getMaterialUnit(specimenType: CryoSpecimenType): "count" | "vial" {
  return specimenType === "sperm" ? "vial" : "count";
}

function getMaterialUnitLabel(
  t: TranslationFunction,
  unit: "count" | "vial",
): string {
  return unit === "vial"
    ? t("cryo_vault.modal.lifecycle_event_unit_vial")
    : t("cryo_vault.modal.lifecycle_event_unit_count");
}

function getLocalEventDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function getLocalEventTime(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

function buildOccurredAt(date: string, time: string): string {
  const offsetMinutes = -new Date(`${date}T${time}:00`).getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
  const minutes = String(absoluteMinutes % 60).padStart(2, "0");
  return `${date}T${time}:00${sign}${hours}:${minutes}`;
}

function isValidQuantity(value: string, unit: "count" | "vial"): boolean {
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value.trim())) return false;
  if (Number(value) <= 0) return false;
  return unit === "count" || unit === "vial" ? !value.includes(".") : true;
}

function isValidPercentage(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(trimmed)) return false;
  const numericValue = Number(trimmed);
  return numericValue >= 0 && numericValue <= 100;
}

function isValidSurvivingQuantity(
  value: string,
  thawedQuantity: string,
): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (!/^\d+$/.test(trimmed)) return false;
  return Number(trimmed) <= Number(thawedQuantity);
}

function parsePercentage(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function getSurvivalRate(
  thawedQuantity: string,
  survivedQuantity: string,
): number | null {
  if (!isValidSurvivingQuantity(survivedQuantity, thawedQuantity)) return null;
  if (!survivedQuantity.trim()) return null;
  return (Number(survivedQuantity) / Number(thawedQuantity)) * 100;
}

function formatPercentage(value: number): string {
  return `${Number.isInteger(value) ? value : Number(value.toFixed(2))}%`;
}

function getActionLabel(
  t: TranslationFunction,
  action: SpecimenMaterialOperationAction,
): string {
  return t(LIFECYCLE_LABEL_KEYS[action]);
}

function getRelatedCycleId(
  specimen: CryoSpecimenDetail,
  action: SpecimenMaterialOperationAction,
): string | null {
  if (action === "transfer") return specimen.transferCycleId ?? null;
  if (
    ["release", "reserve", "thaw", "fertilize", "mature", "use"].includes(
      action,
    )
  ) {
    return specimen.useCycleId ?? null;
  }
  return null;
}

function ChoicePill({
  selected,
  label,
  onPress,
}: {
  selected: boolean;
  label: string;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("LifecycleChoicePill");
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      onPress={() => {
        capturePress(pressLabel(label), "specimen_lifecycle_choice");
        onPress();
      }}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: selected ? EMBER.colors.textLight : "rgba(92,52,32,0.16)",
        backgroundColor: selected
          ? "rgba(92,52,32,0.12)"
          : "rgba(255,255,255,0.62)",
        paddingHorizontal: 11,
        paddingVertical: 8,
      }}
    >
      {selected && (
        <Check size={13} color={EMBER.colors.textLight} strokeWidth={2.5} />
      )}
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.text,
          fontSize: 12,
          lineHeight: 15,
          fontWeight: "600",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function CryoSpecimenLifecycleActionPicker({
  visible,
  specimen,
  onClose,
  onSelect,
}: {
  visible: boolean;
  specimen: CryoSpecimenDetail | null;
  onClose: () => void;
  onSelect: (action: SpecimenMaterialOperationAction) => void;
}) {
  const t = useTranslations();
  if (!visible || !specimen) return null;

  return (
    <Dialog
      visible
      onClose={onClose}
      closeButton="with-background"
      containerStyle={{ width: "90%", maxWidth: 390 }}
      contentStyle={{ paddingTop: 26, paddingHorizontal: 22 }}
      testID="cryo-specimen-lifecycle-picker"
    >
      <DialogHeader
        title={t("cryo_vault.modal.record_lifecycle_event")}
        subtitle={specimen.label}
        style={{ marginBottom: 16 }}
      />
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.textFaint, fontSize: 13, lineHeight: 18 }}
      >
        {t("cryo_vault.modal.lifecycle_event_help")}
      </Text>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 16,
        }}
      >
        {getLifecycleActionsForSpecimen(specimen).map((action) => (
          <ChoicePill
            key={action}
            selected={false}
            label={getActionLabel(t, action)}
            onPress={() => onSelect(action)}
          />
        ))}
      </View>
      <View style={{ marginTop: 20, marginBottom: 2 }}>
        <DialogSecondaryButton
          label={t("appointment_detail.cancel")}
          onPress={onClose}
        />
      </View>
    </Dialog>
  );
}

export function RecordCryoSpecimenLifecycleDialog({
  visible,
  specimen,
  action,
  onClose,
}: {
  visible: boolean;
  specimen: CryoSpecimenDetail | null;
  action: SpecimenMaterialOperationAction | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const recordOperation = useRecordSpecimenMaterialOperation();
  const semenSpecimensQuery = useSpecimens("semen", {
    enabled: visible && action === "fertilize",
  });
  const [eventDate, setEventDate] = useState(getLocalEventDate);
  const [eventTime, setEventTime] = useState(getLocalEventTime);
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const [thawMotility, setThawMotility] = useState("");
  const [thawViability, setThawViability] = useState("");
  const [thawSurvivedQuantity, setThawSurvivedQuantity] = useState("");
  const [semenSpecimenId, setSemenSpecimenId] = useState<string | null>(null);
  const [semenQuantity, setSemenQuantity] = useState("1");
  const [outputLabel, setOutputLabel] = useState("");
  const [eventIdempotencyKey, setEventIdempotencyKey] =
    useState(createIdempotencyKey);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setEventDate(getLocalEventDate());
    setEventTime(getLocalEventTime());
    setQuantity("1");
    setNotes("");
    setThawMotility("");
    setThawViability("");
    setThawSurvivedQuantity("");
    setSemenSpecimenId(null);
    setSemenQuantity("1");
    setOutputLabel("");
    setEventIdempotencyKey(createIdempotencyKey());
    setSaveError(null);
  }, [action, visible]);

  if (!visible || !specimen || !action) return null;

  const unit = getMaterialUnit(specimen.type);
  const isSperm = specimen.type === "sperm";
  const isFertilization = action === "fertilize";
  const isThaw = action === "thaw";
  const isSaving = recordOperation.isPending;
  const semenSpecimens = (semenSpecimensQuery.data ?? []).filter(
    ({ status }) => !["used", "discarded", "donated"].includes(status),
  );
  const selectedSemenSpecimen = semenSpecimens.find(
    ({ id }) => id === semenSpecimenId,
  );
  const percentagesValid =
    isValidPercentage(thawViability) &&
    (!isSperm || isValidPercentage(thawMotility));
  const survivingQuantityValid = isValidSurvivingQuantity(
    thawSurvivedQuantity,
    quantity,
  );
  const thawMeasurementsValid = isSperm
    ? percentagesValid
    : survivingQuantityValid;
  const thawSurvivalRate =
    !isSperm && isThaw ? getSurvivalRate(quantity, thawSurvivedQuantity) : null;
  const fertilizationValid =
    !isFertilization ||
    (Boolean(selectedSemenSpecimen) &&
      isValidQuantity(semenQuantity, "vial") &&
      outputLabel.trim().length > 0);
  const formValid =
    isValidQuantity(quantity, unit) &&
    /^\d{4}-\d{2}-\d{2}$/.test(eventDate.trim()) &&
    /^\d{2}:\d{2}$/.test(eventTime.trim()) &&
    (!isThaw || thawMeasurementsValid) &&
    fertilizationValid;

  const handleSave = async () => {
    if (!formValid) return;
    if (isFertilization && !selectedSemenSpecimen) return;
    setSaveError(null);
    const relatedCycleId = getRelatedCycleId(specimen, action);
    const fertilizationInput =
      isFertilization && selectedSemenSpecimen
        ? {
            specimenId: selectedSemenSpecimen.id,
            quantity: semenQuantity.trim(),
            unit: "vial" as const,
            disposition: "consumed" as const,
          }
        : null;

    const operation: TypedSpecimenMaterialOperation = {
      clientRequestId: eventIdempotencyKey,
      action,
      occurredAt: buildOccurredAt(eventDate.trim(), eventTime.trim()),
      quantity: quantity.trim(),
      unit,
      hasDecisionAndConsent: true,
      ...(relatedCycleId ? { cycleId: relatedCycleId } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(isThaw
        ? {
            thawingInfo: isSperm
              ? {
                  motilityPostThaw: parsePercentage(thawMotility),
                  viability: parsePercentage(thawViability),
                }
              : {
                  ...(thawSurvivedQuantity.trim()
                    ? {
                        survivedQuantity: Number(thawSurvivedQuantity.trim()),
                      }
                    : {}),
                },
          }
        : {}),
      ...(fertilizationInput
        ? {
            additionalInputs: [fertilizationInput],
            outputs: [
              {
                newAliquot: { label: outputLabel.trim() },
                quantity: quantity.trim(),
                unit: "count" as const,
                disposition: "produced" as const,
              },
            ],
          }
        : {}),
    };

    try {
      await recordOperation.mutateAsync({ id: specimen.id, operation });
      onClose();
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : t("cryo_vault.modal.lifecycle_event_error"),
      );
    }
  };

  return (
    <Dialog
      visible
      onClose={isSaving ? () => undefined : onClose}
      closeButton="with-background"
      containerStyle={{ width: "90%", maxWidth: 390, maxHeight: 680 }}
      surfaceStyle={{ maxHeight: 680 }}
      contentStyle={{ paddingTop: 24, paddingHorizontal: 22, maxHeight: 680 }}
      testID="cryo-specimen-lifecycle-dialog"
    >
      <DialogHeader
        title={getActionLabel(t, action)}
        subtitle={specimen.label}
        style={{ marginBottom: 16 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 14, paddingBottom: 4 }}
      >
        <MoonFormField
          label={t("cryo_vault.modal.lifecycle_event_quantity")}
          labelIcon={Activity}
        >
          <MoonFieldInput paddingY={10}>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <TextInput
                accessibilityLabel={t(
                  "cryo_vault.modal.lifecycle_event_quantity",
                )}
                value={quantity}
                onChangeText={setQuantity}
                placeholder={t(
                  "cryo_vault.modal.lifecycle_event_quantity_placeholder",
                )}
                placeholderTextColor={EMBER.colors.textFaint}
                keyboardType="decimal-pad"
                style={{ flex: 1, color: EMBER.colors.text, fontSize: 14 }}
              />
              <Text
                style={{
                  color: EMBER.colors.textMid,
                  fontSize: 13,
                  fontWeight: "600",
                }}
              >
                {getMaterialUnitLabel(t, unit)}
              </Text>
            </View>
          </MoonFieldInput>
        </MoonFormField>

        {isFertilization && (
          <View style={{ gap: 12 }}>
            <MoonFormField
              label={t("cryo_vault.modal.fertilization_source_label")}
              labelIcon={Activity}
            >
              <View style={{ gap: 8 }}>
                {semenSpecimensQuery.isLoading ? (
                  <ActivityIndicator
                    color={EMBER.colors.textLight}
                    size="small"
                  />
                ) : semenSpecimens.length > 0 ? (
                  semenSpecimens.map((semenSpecimen, index) => (
                    <ChoicePill
                      key={semenSpecimen.id}
                      selected={semenSpecimenId === semenSpecimen.id}
                      label={
                        semenSpecimen.sampleId?.trim() ||
                        t("cryo_vault.labels.sample_label").replace(
                          "${n}",
                          String(index + 1),
                        )
                      }
                      onPress={() => setSemenSpecimenId(semenSpecimen.id)}
                    />
                  ))
                ) : (
                  <Text
                    style={{
                      color: EMBER.colors.textFaint,
                      fontSize: 12,
                      lineHeight: 17,
                    }}
                  >
                    {t("cryo_vault.modal.fertilization_source_empty")}
                  </Text>
                )}
              </View>
            </MoonFormField>
            <MoonFormField
              label={t("cryo_vault.modal.fertilization_source_quantity")}
              labelIcon={Activity}
            >
              <MoonFieldInput paddingY={10}>
                <TextInput
                  accessibilityLabel={t(
                    "cryo_vault.modal.fertilization_source_quantity",
                  )}
                  value={semenQuantity}
                  onChangeText={setSemenQuantity}
                  keyboardType="decimal-pad"
                  style={{ color: EMBER.colors.text, fontSize: 14 }}
                />
              </MoonFieldInput>
            </MoonFormField>
            <MoonFormField
              label={t("cryo_vault.modal.fertilization_output_label")}
              labelIcon={FileText}
            >
              <MoonFieldInput paddingY={10}>
                <TextInput
                  accessibilityLabel={t(
                    "cryo_vault.modal.fertilization_output_label",
                  )}
                  value={outputLabel}
                  onChangeText={setOutputLabel}
                  placeholder={t(
                    "cryo_vault.modal.fertilization_output_placeholder",
                  )}
                  placeholderTextColor={EMBER.colors.textFaint}
                  style={{ color: EMBER.colors.text, fontSize: 14 }}
                />
              </MoonFieldInput>
            </MoonFormField>
          </View>
        )}

        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <MoonDateField
              label={t("cryo_vault.modal.lifecycle_event_date")}
              labelIcon={CalendarDays}
              value={eventDate}
              onChange={setEventDate}
              placeholder={t(
                "cryo_vault.modal.lifecycle_event_date_placeholder",
              )}
            />
          </View>
          <View style={{ flex: 1 }}>
            <MoonTimeField
              label={t("cryo_vault.modal.lifecycle_event_time")}
              labelIcon={Clock3}
              value={eventTime}
              onChange={setEventTime}
              placeholder={t(
                "cryo_vault.modal.lifecycle_event_time_placeholder",
              )}
            />
          </View>
        </View>

        {isThaw && (
          <View style={{ gap: 12 }}>
            {isSperm ? (
              <View style={{ flexDirection: "row", gap: 10 }}>
                <View style={{ flex: 1 }}>
                  <MoonFormField
                    label={t("cryo_vault.modal.lifecycle_thaw_motility")}
                    labelIcon={Activity}
                  >
                    <MoonFieldInput paddingY={10}>
                      <TextInput
                        accessibilityLabel={t(
                          "cryo_vault.modal.lifecycle_thaw_motility",
                        )}
                        value={thawMotility}
                        onChangeText={setThawMotility}
                        keyboardType="decimal-pad"
                        style={{ color: EMBER.colors.text, fontSize: 14 }}
                      />
                    </MoonFieldInput>
                  </MoonFormField>
                </View>
                <View style={{ flex: 1 }}>
                  <MoonFormField
                    label={t("cryo_vault.modal.lifecycle_thaw_viability")}
                    labelIcon={Activity}
                  >
                    <MoonFieldInput paddingY={10}>
                      <TextInput
                        accessibilityLabel={t(
                          "cryo_vault.modal.lifecycle_thaw_viability",
                        )}
                        value={thawViability}
                        onChangeText={setThawViability}
                        keyboardType="decimal-pad"
                        style={{ color: EMBER.colors.text, fontSize: 14 }}
                      />
                    </MoonFieldInput>
                  </MoonFormField>
                </View>
              </View>
            ) : (
              <>
                <MoonFormField
                  label={t(
                    "cryo_vault.modal.lifecycle_thaw_surviving_quantity",
                  )}
                  labelIcon={Activity}
                >
                  <MoonFieldInput paddingY={10}>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <TextInput
                        accessibilityLabel={t(
                          "cryo_vault.modal.lifecycle_thaw_surviving_quantity",
                        )}
                        value={thawSurvivedQuantity}
                        onChangeText={setThawSurvivedQuantity}
                        placeholder={t(
                          "cryo_vault.modal.lifecycle_thaw_surviving_quantity_placeholder",
                        )}
                        placeholderTextColor={EMBER.colors.textFaint}
                        keyboardType="number-pad"
                        style={{
                          flex: 1,
                          color: EMBER.colors.text,
                          fontSize: 14,
                        }}
                      />
                      <Text
                        style={{
                          color: EMBER.colors.textMid,
                          fontSize: 13,
                          fontWeight: "600",
                        }}
                      >
                        {getMaterialUnitLabel(t, unit)}
                      </Text>
                    </View>
                  </MoonFieldInput>
                </MoonFormField>
                <MoonFormField
                  label={t("cryo_vault.modal.lifecycle_thaw_survival_rate")}
                  labelIcon={Activity}
                >
                  <View
                    accessibilityLabel={t(
                      "cryo_vault.modal.lifecycle_thaw_survival_rate",
                    )}
                    style={{
                      borderWidth: 1,
                      borderColor: "rgba(92,52,32,0.12)",
                      borderRadius: 12,
                      backgroundColor: "rgba(92,52,32,0.05)",
                      paddingHorizontal: 14,
                      paddingVertical: 11,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{ color: EMBER.colors.text, fontSize: 14 }}
                    >
                      {thawSurvivalRate === null
                        ? t(
                            "cryo_vault.modal.lifecycle_thaw_survival_rate_unrecorded",
                          )
                        : formatPercentage(thawSurvivalRate)}
                    </Text>
                  </View>
                </MoonFormField>
              </>
            )}
            {isThaw && !thawMeasurementsValid && (
              <Text
                accessibilityRole="alert"
                style={{ color: "#B42318", fontSize: 12, lineHeight: 17 }}
              >
                {isSperm
                  ? t("cryo_vault.modal.lifecycle_thaw_percentage_error")
                  : t(
                      "cryo_vault.modal.lifecycle_thaw_surviving_quantity_error",
                    )}
              </Text>
            )}
          </View>
        )}

        <MoonFormField
          label={t("cryo_vault.modal.lifecycle_event_note")}
          labelIcon={FileText}
        >
          <MoonFieldInput paddingY={10}>
            <TextInput
              accessibilityLabel={t("cryo_vault.modal.lifecycle_event_note")}
              value={notes}
              onChangeText={setNotes}
              placeholder={t(
                "cryo_vault.modal.lifecycle_event_note_placeholder",
              )}
              placeholderTextColor={EMBER.colors.textFaint}
              multiline
              textAlignVertical="top"
              style={{ minHeight: 76, color: EMBER.colors.text, fontSize: 14 }}
            />
          </MoonFieldInput>
        </MoonFormField>

        {(!formValid || saveError) && (
          <Text
            accessibilityRole="alert"
            style={{ color: "#B42318", fontSize: 12, lineHeight: 17 }}
          >
            {saveError ??
              (isFertilization
                ? t("cryo_vault.modal.fertilization_required")
                : t("cryo_vault.modal.lifecycle_event_required"))}
          </Text>
        )}
      </ScrollView>
      <View
        style={{
          flexDirection: "row",
          gap: 8,
          paddingTop: 14,
          marginTop: 16,
        }}
      >
        <DialogSecondaryButton
          flex={1}
          label={t("appointment_detail.cancel")}
          onPress={onClose}
          disabled={isSaving}
        />
        <DialogPrimaryButton
          flex={1.35}
          label={
            isSaving
              ? t("cryo_vault.modal.saving_changes")
              : t("cryo_vault.modal.record_lifecycle_event_button")
          }
          onPress={handleSave}
          disabled={isSaving || !formValid}
          icon={
            isSaving ? (
              <ActivityIndicator color={EMBER.colors.textLight} size="small" />
            ) : undefined
          }
        />
      </View>
    </Dialog>
  );
}
